import { withSearch } from "@elastic/react-search-ui";
import { MultiCheckboxFacet } from "@elastic/react-search-ui-views";
import { type FacetValue, type Filter, helpers } from "@elastic/search-ui";
import { ComponentType, useCallback, useMemo, useState } from "react";

import { FacetViewProps } from "../interface";
import { accentFold } from "../utils";
import {
  FacetContainerContext,
  FacetContainerProps,
  SelectedValue
} from "./interface";

const { markSelectedFacetValuesFromFilters } = helpers;

const CustomFacet = ({
  addFilter,
  className,
  facets,
  field,
  filterType = "all",
  filters,
  label,
  removeFilter,
  setFilter,
  view,
  show = 100,
  isFilterable = false,
  collapsedByDefault = false,
  a11yNotify,
  sortAlphabetically,
  showDisabledOptions = true,
  ...rest
}: FacetContainerProps & FacetContainerContext) => {
  const [more, setMore] = useState<number>(show);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const handleClickMore = useCallback(
    (totalOptions: number) => {
      setMore((moreMore) => {
        let visibleOptionsCount = moreMore + 10;
        const showingAll = visibleOptionsCount >= totalOptions;
        if (showingAll) visibleOptionsCount = totalOptions;

        a11yNotify("moreFilters", { visibleOptionsCount, showingAll });

        return visibleOptionsCount;
      });
    },
    [a11yNotify]
  );

  const handleFacetSearch = useCallback((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  }, []);

  const handleRemove = useCallback(
    (value: FacetViewProps["values"][number]) => {
      removeFilter(field, value, filterType);
    },
    [removeFilter, field, filterType]
  );

  const handleChange = useCallback(
    (value: FacetViewProps["values"][number]) => {
      setFilter(field, value, filterType);
    },
    [setFilter, field, filterType]
  );

  const handleSelect = useCallback(
    (value: FacetViewProps["values"][number]) => {
      addFilter(field, value, filterType);
    },
    [addFilter, field, filterType]
  );

  const facetsForField = facets[field];

  const facetValues = useMemo(() => {
    if (!facetsForField) return [];

    const facet = facetsForField[0];
    let values = markSelectedFacetValuesFromFilters(
      facet,
      filters as Filter[],
      field,
      filterType
    ).data;

    if (searchTerm.trim()) {
      const normalizedSearch = accentFold(searchTerm).toLowerCase();
      values = values.filter((option: FacetValue) => {
        let valueToSearch;
        switch (typeof option.value) {
          case "string": {
            valueToSearch = accentFold(option.value).toLowerCase();
            break;
          }
          case "number": {
            valueToSearch = option.value.toString();
            break;
          }
          case "object": {
            valueToSearch =
              typeof option?.value === "string"
                ? accentFold(option.value).toLowerCase()
                : "";
            break;
          }
          default: {
            valueToSearch = "";
            break;
          }
        }
        return valueToSearch.includes(normalizedSearch);
      });
    }

    return values;
  }, [facetsForField, filters, field, filterType, searchTerm]);

  const selectedValues = useMemo<SelectedValue[]>(
    () =>
      facetValues
        .filter((fv: { selected: boolean }) => fv.selected)
        .map((fv: { value: string }) => fv.value),
    [facetValues]
  );

  const options = useMemo(
    () => facetValues.slice(0, more),
    [facetValues, more]
  );

  if (!facetsForField) return null;

  const View: ComponentType<FacetViewProps> = view || MultiCheckboxFacet;

  const viewProps: FacetViewProps = {
    className,
    label,
    onMoreClick: () => handleClickMore(facetValues.length),
    onRemove: handleRemove,
    onChange: handleChange,
    onSelect: handleSelect,
    options,
    showMore: facetValues.length > more,
    showOptionsCount: show,
    values: selectedValues,
    showSearch: isFilterable!,
    onSearch: handleFacetSearch,
    searchPlaceholder: `Søk i ${label}`,
    sortAlphabetically: sortAlphabetically!,
    showDisabledOptions: showDisabledOptions!,
    collapsedByDefault,
    ...rest
  };

  return <View {...viewProps} />;
};

export default withSearch<FacetContainerProps, FacetContainerContext>(
  ({ filters, facets, addFilter, removeFilter, setFilter, a11yNotify }) => ({
    filters,
    facets,
    addFilter,
    removeFilter,
    setFilter,
    a11yNotify
  })
)(CustomFacet);
